import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, ShieldCheck, Settings } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import { DynamicLogo } from '../../components/DynamicLogo';

export function SSO() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [message, setMessage] = useState('Autenticando via SSO...');

  const { loginWithSSO } = useAuth();

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Token SSO não fornecido');
      setTimeout(() => navigate('/admin/login', { replace: true }), 3000);
      return;
    }

    async function processSSO() {
      try {
        const result = await loginWithSSO(token!);

        if (result?.user?.role === 'admin') {
          navigate('/dashboard', { replace: true });
        } else {
          setStatus('error');
          setMessage('Acesso restrito a administradores');
          setTimeout(() => navigate('/admin/login', { replace: true }), 3000);
        }
      } catch (err: any) {
        setStatus('error');
        setMessage(err?.response?.data?.error || err?.message || 'Falha na autenticação SSO');
        setTimeout(() => navigate('/admin/login', { replace: true }), 3000);
      }
    }

    processSSO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  return (
    <>
      <Helmet>
        <title>Autenticando | Portal</title>
      </Helmet>

      <div className="min-h-screen bg-background flex items-center justify-center py-20 px-4">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-brand/5 rounded-full blur-3xl" />
        </div>

        <div className="w-full max-w-md relative z-10">
          <div className="p-8 rounded-2xl bg-card border border-border shadow-lg">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <DynamicLogo
                  area="login"
                  alt="Admin Portal"
                  className="h-16 w-auto"
                  fallback={
                    <div className="inline-flex p-4 rounded-xl bg-foreground">
                      <Settings className="w-10 h-10 text-background" />
                    </div>
                  }
                />
              </div>
              <h1 className="text-2xl font-bold">
                {status === 'processing' ? 'Autenticando via SSO' : 'Erro na Autenticação'}
              </h1>
              <p className="text-muted-foreground text-sm mt-2">
                {status === 'processing'
                  ? 'Aguarde enquanto validamos seu acesso'
                  : message}
              </p>
            </div>

            <div className="flex items-center justify-center gap-3">
              {status === 'processing' ? (
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
              ) : (
                <div className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="w-8 h-8" />
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center gap-2 text-muted-foreground text-xs justify-center">
                <ShieldCheck className="w-4 h-4" />
                <span>Conexão segura via Zooy OS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
